import React, { ReactElement } from "react";
import classNames from "classnames";
import { injectStripe, CardElement, ReactStripeElements } from "react-stripe-elements";

import Button from "components/Common/Button";
import { cssColors } from "constants/css";
import { ModalBillingPaymentRecord } from "../services";
import "./style.scss";

/** Returns styles for Stripe element inputs */
function getStripeInputStyles(): Record<string, unknown> {
  return {
    style: {
      base: {
        fontSize: "16px",
        color: cssColors.colorBlack,
        lineHeight: "26px",
        fontFamily: "-apple-system, BlinkMacSystemFont, \"Apple Color Emoji, \"Segoe UI\", \"Segoe UI Emoji\", \"Segoe UI Symbol\", Roboto, Helvetica, Arial, sans-serif\"",
        fontWeight: "300"
      }
    }
  };
}

interface OwnProps {
  modalBillingPayment: ModalBillingPaymentRecord;
  setState(name: string, payload: Record<string, unknown>): void;
  postCreditCard(modalBillingPayment: ModalBillingPaymentRecord, stripeToken: string): void;
  patchCreditCard(modalBillingPayment: ModalBillingPaymentRecord, stripeToken: string): void;
}

export type ModalBillingPaymentFormProps = OwnProps & ReactStripeElements.InjectedStripeProps;

class ModalBillingPaymentForm extends React.Component<ModalBillingPaymentFormProps> {
  handleMakeRequestBound = this.handleMakeRequest.bind(this);

  /** Handles input changes for Stripe and non-Stripe inputs */
  handleInputChange(
    field: string,
    isStripeElement: boolean,
    e: React.KeyboardEvent<HTMLInputElement> | ReactStripeElements.ElementChangeResponse
  ): void {
    const { setState } = this.props;
    const objectToMerge: {[key: string]: unknown} = {};

    if (!isStripeElement) {
      objectToMerge[field] = (e as React.KeyboardEvent<HTMLInputElement>).currentTarget.value;
    } else {
      const { error, complete } = e as ReactStripeElements.ElementChangeResponse;
      objectToMerge.isCardValid = !error && complete;
    }

    setState("MODAL_BILLING_PAYMENT", objectToMerge);
  }

  /** Makes the tokenization request to Stripe and handles the response */
  handleMakeRequest(e: React.MouseEvent): void {
    const {
      stripe, setState, modalBillingPayment, postCreditCard, patchCreditCard
    } = this.props;

    e.preventDefault();

    if (stripe) {
      setState("MODAL_BILLING_PAYMENT", { isLoading: true });

      // Create the tokenization request (Stripe)
      stripe
        .createToken({
          name: modalBillingPayment.get("cardholderName"),
          address_line1: modalBillingPayment.get("address"),
          address_city: modalBillingPayment.get("city"),
          address_country: modalBillingPayment.get("country"),
          address_zip: modalBillingPayment.get("postalCode")
        })
        .then((payload) => {
          const stripeToken = payload.token.id;

          if (modalBillingPayment.get("newCard")) {
            postCreditCard(
              modalBillingPayment,
              stripeToken
            );
          } else {
            patchCreditCard(
              modalBillingPayment,
              stripeToken
            );
          }
        })
        .catch(() => {
          setState("MODAL_BILLING_PAYMENT", { isLoading: false });
        });
    } else {
      console.warn("Stripe.js hasn't loaded yet.");
    }
  }

  /** Returns a form input based on input args */
  renderAddressInput(
    label: string,
    placeholder: string,
    key: "cardholderName" | "address" | "postalCode" | "city" | "country"
  ): ReactElement {
    const { modalBillingPayment } = this.props;

    return (
      <div className="ModalBillingPaymentForm__section__input-container__input">
        <label
          className="g-input-label"
          htmlFor={`ModalBillingPaymentForm__${key}`}
        >
          {label}
        </label>
        <input
          type="text"
          className={classNames("g-input", {
            "m-state_invalid": !modalBillingPayment.get(key)
          })}
          placeholder={placeholder}
          value={modalBillingPayment.get(key)}
          onChange={this.handleInputChange.bind(this, key, false)}
          id={`ModalBillingPaymentForm__${key}`}
        />
      </div>
    );
  }

  render(): ReactElement {
    const { modalBillingPayment } = this.props;

    return (
      <div className="ModalBillingPaymentForm">
        <div className="Modal__content">
          <form>
            <div className="ModalBillingPaymentForm__section__input-container">
              {this.renderAddressInput(
                "Cardholder Name",
                "Jane Doe",
                "cardholderName"
              )}
              {this.renderAddressInput(
                "Address",
                "123 Fake St, Unit 1",
                "address"
              )}
            </div>
            <div className="ModalBillingPaymentForm__section__input-container">
              {this.renderAddressInput("City", "Toronto", "city")}
              {this.renderAddressInput("Country", "Canada", "country")}
              {this.renderAddressInput(
                "Postal Code / Zip Code",
                "M5T 2C2",
                "postalCode"
              )}
            </div>
            <div className="ModalBillingPaymentForm__section__input-container">
              <div className="ModalBillingPaymentForm__section__input-container__input">
                {/* Label is wrapping an input */}
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className="g-input-label ModalBillingPaymentForm__section__input-container__input__stripe-label">
                  Credit Card
                  <CardElement
                    onChange={e => this.handleInputChange("card", true, e)}
                    hidePostalCode
                    {...getStripeInputStyles()}
                    className={classNames({
                      "StripeElement--card-invalid": !modalBillingPayment.get("isCardValid")
                    })}
                  />
                </label>
              </div>
            </div>
          </form>
        </div>
        <div className="Modal__bottom ModalBillingPaymentForm__bottom">
          <Button
            title={
              modalBillingPayment.get("newCard")
                ? "Add Credit Card"
                : "Update Credit Card"
            }
            text={
              modalBillingPayment.get("newCard")
                ? "Add Credit Card"
                : "Update Credit Card"
            }
            icon="Cloud"
            fillColor="White"
            isLoading={modalBillingPayment.get("isLoading")}
            disabled={
              modalBillingPayment.get("isLoading") ||
              !modalBillingPayment.get("canSubmit")
            }
            onClick={this.handleMakeRequestBound}
          />
        </div>
      </div>
    );
  }
}

const StripeInjectedComponent = injectStripe(ModalBillingPaymentForm);

export { ModalBillingPaymentForm };
export default StripeInjectedComponent;
